import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
    SURVEYS_EDIT,
    SURVEYS_RENDER,
    SURVEYS_DETAIL,
} from '../../../../../../routes/paths';
import {
    asyncDuplicateSurvey,
    asyncUpdateSurvey,
} from '../../../../../../services/survey.service';
import { ToastSwal } from '../../../../../components/general/toastSwals/toastSwal';

export const useSurveyListActions = (
    id: string,
    status: string,
    onRefresh: () => void
) => {
    const navigate = useNavigate();
    const [duplicating, setDuplicating] = useState(false);
    const [toggling, setToggling] = useState(false);

    const handleView = () => {
        navigate(`${SURVEYS_DETAIL}/${id}`);
    };

    const handleEdit = () => {
        navigate(`${SURVEYS_EDIT}/${id}`);
    };

    const handleOpenSurvey = () => {
        // Abrir en una nueva pestaña
        window.open(`${SURVEYS_RENDER}/${id}`, '_blank');
    };

    const handleDuplicate = async () => {
        if (duplicating) return;

        const result = await Swal.fire({
            icon: 'question',
            title: '¿Duplicar encuesta?',
            text: 'Se creará una copia de la encuesta en estado inactivo con el nombre " - Copia"',
            showCancelButton: true,
            confirmButtonText: 'Sí, duplicar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#6a1b9a',
            cancelButtonColor: '#d33',
        });

        if (!result.isConfirmed) return;

        setDuplicating(true);
        try {
            await asyncDuplicateSurvey(id);
            ToastSwal.fire({
                timer: 3000,
                text: 'Encuesta duplicada exitosamente',
                icon: 'success',
            });
            // Recargar la lista
            setTimeout(() => {
                onRefresh();
            }, 1000);
        } catch (error) {
            console.error('Error al duplicar encuesta:', error);
            ToastSwal.fire({
                timer: 3000,
                text: 'Error al duplicar la encuesta. Por favor, intenta de nuevo.',
                icon: 'error',
            });
        } finally {
            setDuplicating(false);
        }
    };

    const handleToggleStatus = async () => {
        if (toggling) return;

        const newStatus = status === 'active' ? 'inactive' : 'active';
        const actionText = newStatus === 'active' ? 'activar' : 'inactivar';

        const result = await Swal.fire({
            icon: 'question',
            title: `¿${
                actionText.charAt(0).toUpperCase() + actionText.slice(1)
            } encuesta?`,
            text: `¿Está seguro que desea ${actionText} esta encuesta?`,
            showCancelButton: true,
            confirmButtonText: `Sí, ${actionText}`,
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#6a1b9a',
            cancelButtonColor: '#d33',
        });

        if (!result.isConfirmed) return;

        setToggling(true);
        try {
            await asyncUpdateSurvey(id, { status: newStatus });
            ToastSwal.fire({
                timer: 3000,
                text: `Encuesta ${
                    newStatus === 'active' ? 'activada' : 'inactivada'
                } exitosamente`,
                icon: 'success',
            });
            // Recargar la lista
            setTimeout(() => {
                onRefresh();
            }, 1000);
        } catch (error) {
            console.error('Error al cambiar estado de encuesta:', error);
            ToastSwal.fire({
                timer: 3000,
                text: 'Error al cambiar el estado de la encuesta. Por favor, intenta de nuevo.',
                icon: 'error',
            });
        } finally {
            setToggling(false);
        }
    };

    return {
        handleView,
        handleEdit,
        handleOpenSurvey,
        handleDuplicate,
        handleToggleStatus,
        duplicating,
        toggling,
        isActive: status === 'active',
    };
};
